import {ObjectOfAny} from '../types';

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export const isObject = (item: unknown): boolean => !!(item && typeof item === 'object' && !Array.isArray(item));

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export const mergeDeep = (target: ObjectOfAny, ...sources: ObjectOfAny[]): ObjectOfAny => {
  // no new sources end recursion
  if (!sources.length) {
    return target;
  }

  // check sources one by one from from of array
  const source = sources.shift() as ObjectOfAny;

  // if current source and target are both objects we need to traverse into them
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      // each key on the source that is an object
      if (isObject(source[key])) {
        // use source object if target doesn't have key create it
        if (!target[key]) {
          Object.assign(target, {[key]: {}});
        }
        // recursion: go deeper and merge object on key before continuing
        mergeDeep(target[key], source[key]);
      } else {
        // source is not an object so set its key value on target
        Object.assign(target, {[key]: source[key]});
      }
    });
  }

  // recursion: continue with remaining sources
  return mergeDeep(target, ...sources);
};

export const getObjectKeys = Object.keys as <ObjectType>(object: ObjectType) => Extract<keyof ObjectType, string>[];

export const flattenObject = (object: ObjectOfAny): ObjectOfAny =>
  Object.assign(
    {},
    ...(function _flatten(objectBit: ObjectOfAny, path = ''): ObjectOfAny[] {
      return ([] as ObjectOfAny[]).concat(
        ...Object.keys(objectBit).map((key) => {
          const flattenedKey =
            typeof objectBit[key] === 'object'
              ? _flatten(objectBit[key], `${path}.${key}`)
              : {[`${path}.${key}`.replace(/^./, '')]: objectBit[key]};
          return flattenedKey;
        })
      );
    })(object)
  );
